import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, Sheet, Text } from '../../../../components';
import { COLORS, EMAIL } from '../../../../theme/tokens';
import { formatAmountWithCode } from '../../../../utils/money';
import { mailboxWalletCovers, type MailboxPriceView } from '../../state';
import type { MailboxPaymentMethodId } from '../../usePurchase';
import {
  HeaderId,
  IconTile,
  HeaderText,
  Name,
  Price,
  OptionCard,
  OptionRow,
  Radio,
  OptionText,
  OptionLabel,
  OptionHint,
  TotalRow,
  TotalLabel,
  TotalValue,
  ErrorLine,
} from './PaymentSheet.styles';

export type PaymentSheetProps = {
  open: boolean;
  /** What the service said a paid mailbox costs (see `mailboxPriceView`). Only `priced` can be paid:
   *  without it nothing shows a number and Pay stays disabled, and the sheet says why when the read
   *  failed. (Charging switched off never opens this sheet.) */
  price: MailboxPriceView;
  /** The wallet's available balance in the price currency, or null while unknown (then nothing is
   *  blocked); unmatched wallets arrive as null too, so an unmatched wallet never pays. */
  walletBalance: number | null;
  /** True while a settlement is in flight: the controls disable and the sheet holds open. */
  paying: boolean;
  /** A settlement that failed, shown in the sheet; null when there is nothing to say. */
  error: string | null;
  /** Pay by the chosen method. The caller mints, settles and continues. */
  onPay: (method: MailboxPaymentMethodId) => void;
  /** The X: dismiss without paying. Nothing was minted, so nothing is left pending. */
  onClose: () => void;
};

/**
 * The checkout sheet for a paid mailbox: the house activation sheet with ONE price, the service's,
 * charged once. There is no term to choose, so the header price, the wallet row's deduction line,
 * Total due today and the Pay button all state the same figure, and none of them says it again later.
 *
 * The wallet is the default method; a short balance forces the card, DERIVED not stored
 * (`walletShort ? 'card' : method`), so nothing can go stale. Every open resets the method so no
 * stale choice survives a reopen.
 *
 * Nothing is minted before this sheet opens: the invoice is minted only when Pay is pressed, and the
 * mailbox is created only after it settles (pay, then create), so the X leaves nothing behind: no
 * half-made mailbox, no pending invoice to reconcile.
 */
export const PaymentSheet = ({
  open,
  price,
  walletBalance,
  paying,
  error,
  onPay,
  onClose,
}: PaymentSheetProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const [method, setMethod] = React.useState<MailboxPaymentMethodId>('wallet');

  // Only a price the service answered is shown or paid; anything else is a dash and a closed Pay.
  const priced = price.kind === 'priced' ? price : null;
  const amountText = priced ? priced.label : '–';
  const balanceText = walletBalance === null || !priced ? null : formatAmountWithCode(walletBalance, priced.currency, 2);
  // Can the balance cover the amount? While unknown (null) nothing is blocked.
  const walletShort = walletBalance !== null && priced !== null && !mailboxWalletCovers(walletBalance, true, priced.amount);
  // A short wallet forces the card path, whatever the last choice was: derived, not stored.
  const selectedMethod: MailboxPaymentMethodId = walletShort ? 'card' : method;

  // The component stays mounted while closed: reset so every open starts on the wallet option.
  React.useEffect(() => {
    if (!open) setMethod('wallet');
  }, [open]);

  const onSelectWallet = React.useCallback(() => setMethod('wallet'), []);
  const onSelectCard = React.useCallback(() => setMethod('card'), []);
  const onPayClick = React.useCallback(() => {
    if (paying || !priced) return;
    onPay(selectedMethod);
  }, [paying, priced, selectedMethod, onPay]);

  if (!open) return null;

  return (
    <Sheet
      ariaLabel="Paid mailbox"
      maxHeight="100%"
      onClose={onClose}
      busy={paying}
      header={
        <HeaderId>
          <IconTile>
            <Icon name="mail" size={24} color={EMAIL.accent} />
          </IconTile>
          <HeaderText>
            <Name text="Paid mailbox" tag="span" />
            <Price text={amountText} tag="span" />
          </HeaderText>
        </HeaderId>
      }
    >
      <OptionCard>
        <OptionRow
          type="button"
          role="radio"
          aria-checked={selectedMethod === 'wallet'}
          onClick={onSelectWallet}
          disabled={walletShort || paying}
          $disabled={walletShort}
          data-testid="mailbox-pay-wallet"
        >
          <Radio $selected={selectedMethod === 'wallet'} aria-hidden="true">
            {selectedMethod === 'wallet' ? <Icon name="check" size={10} color={COLORS.onAccent} /> : null}
          </Radio>
          <OptionText>
            {balanceText !== null ? (
              <OptionLabel text="Your wallet · ({{balance}})" placeholders={{ balance: balanceText }} tag="span" />
            ) : (
              <OptionLabel text="Your wallet" tag="span" />
            )}
            {walletShort && balanceText !== null ? (
              <OptionHint
                text="Not enough · {{amount}} available"
                placeholders={{ amount: balanceText }}
                tag="span"
                $warn
              />
            ) : priced ? (
              <OptionHint text="We will deduct {{price}} from your balance, once" placeholders={{ price: amountText }} tag="span" />
            ) : (
              <OptionHint text="Paid from your balance" tag="span" />
            )}
          </OptionText>
        </OptionRow>
      </OptionCard>

      <OptionCard>
        <OptionRow
          type="button"
          role="radio"
          aria-checked={selectedMethod === 'card'}
          onClick={onSelectCard}
          disabled={paying}
          data-testid="mailbox-pay-card"
        >
          <Radio $selected={selectedMethod === 'card'} aria-hidden="true">
            {selectedMethod === 'card' ? <Icon name="check" size={10} color={COLORS.onAccent} /> : null}
          </Radio>
          <OptionText>
            <OptionLabel text="Credit card or other options" tag="span" />
            <OptionHint text="Pick your preferred method" tag="span" />
          </OptionText>
        </OptionRow>
      </OptionCard>

      <TotalRow>
        <TotalLabel text="Total due today" tag="span" />
        <TotalValue data-testid="mailbox-total-due">{amountText}</TotalValue>
      </TotalRow>

      <Button
        variant="primary"
        fullWidth
        disabled={paying || !priced}
        label={
          paying ? t('Processing…') : priced ? t('Pay {{price}} to activate', { price: amountText }) : t('Pay to activate')
        }
        onClick={onPayClick}
      />

      {error ? (
        <ErrorLine role="alert" data-testid="mailbox-pay-error">
          <Text text={error} tag="span" />
        </ErrorLine>
      ) : price.kind === 'unknown' && price.failed ? (
        <ErrorLine role="alert" data-testid="mailbox-price-unavailable">
          <Text text="The price could not be read, so nothing can be charged. Open Mailbox again to retry." tag="span" />
        </ErrorLine>
      ) : null}
    </Sheet>
  );
};

export default PaymentSheet;
